/*
 * File: AvatarStep.jsx
 * Project: lets-talk-web
 *
 * Created by Brendan Michaelsen on February 26, 2022 at 6:24 PM
 * Copyright © 2022 Let's Talk. All rights reserved.
 *
 * Last Modified: May 16, 2024 at 11:47 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { forwardRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import validator from 'validator';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Utilities
import { toastError } from '../../../../utilities/toaster';

// Services
import { updateUserProfile } from '../../../../services/user';

// Store
import { updateUser } from '../../../../store/slices/user/user.slice';

// Components
import {
	Button, Card, Typography
} from '../../../../components';

// Constants
import { AVATAR_OPTIONS, ROLES } from '../../../../../Constants';

// Styles
import * as S from './AvatarStep.styles';


/**
 * Component
 */

export const AvatarStep = forwardRef(({
	updateStep, role, className
}, ref) => {

	// Create state handlers
	const [isLoading, setIsLoading] = useState(false);
	const [selectedAvatarId, setSelectedAvatarId] = useState(null);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Get action from hooks
	const dispatch = useDispatch();

	// Handle next function
	const handleNext = async () => {

		// Validate parameters
		if (!selectedAvatarId || validator.isEmpty(selectedAvatarId, { ignore_whitespace: true })) {
			toastError(uiMode, 'Please select an avatar to continue', { duration: 7000 });
			return;
		}

		// Set loading state
		setIsLoading(true);

		// Update user profile
		updateUserProfile({
			avatarId: selectedAvatarId
		}).then(async ({ data: { user: newUser } }) => {

			// Update user state
			dispatch(updateUser(newUser));

			// Update step
			if (role === ROLES.PARENT || role === ROLES.ADMIN) {
				updateStep(6);
			} else {
				updateStep(5);
			}

			// Set loading state
			setIsLoading(false);

		}).catch(({ response }) => {

			// Set loading state
			setIsLoading(false);

			// Show error message
			if (response?.data?.message) {
				toastError(uiMode, response?.data?.message, { duration: 7000 });
			} else {
				toastError(uiMode, 'Whoops. We\'re having trouble saving your information. Give it another go.', { duration: 7000 });
			}
		});
	};

	// Handle component initialization
	useEffect(() => {}, []);

	// Return component
	return (
		<S.StepContainer ref={ref} className={className}>

			{/* Card */}
			<Card className="cardPadding">

				{/* Icon */}
				<S.IconContainer>
					<FontAwesomeIcon icon={['fal', 'user']} />
				</S.IconContainer>

				{/* Title */}
				<Typography tag="h3" weight="bold">Select Your Avatar</Typography>

				{/* Instructions */}
				<Typography tag="p" variation="1" weight="medium">Choose an avatar you&apos;d like to use for your journey. (There are only a few to pick from now...more will be coming soon!)</Typography>

				{/* Avatar Selection */}
				<S.SelectionContainer>
					{AVATAR_OPTIONS[role].map((avatarOption) => (
						<S.AvatarOption
							key={avatarOption.id}
							$isSelected={selectedAvatarId === avatarOption.id}
							onClick={() => { setSelectedAvatarId(avatarOption.id); }}
							className="animate"
						>
							<S.AvatarImage src={avatarOption.image} />
							<S.AvatarDecoration />
						</S.AvatarOption>
					))}
				</S.SelectionContainer>

				{/* Next Button */}
				<Button disabled={isLoading} isLoading={isLoading} onClick={handleNext} size="large">Next</Button>

				{/* Back Button */}
				<Button
					disabled={isLoading}
					onClick={() => { updateStep(role === ROLES.PARENT || role === ROLES.ADMIN ? 4 : 3); }}
					size="small"
					variant="text"
					style={{ margin: '10px 0px -5px' }}
				>
					<FontAwesomeIcon icon={['fas', 'arrow-left']} />
					<Typography variation="button-medium" weight="semibold">Go Back</Typography>
				</Button>

			</Card>
		</S.StepContainer>
	);
});


/**
 * Configuration
 */

AvatarStep.displayName = 'AvatarStep';
AvatarStep.propTypes = {
	updateStep: PropTypes.func.isRequired,
	className: PropTypes.string,
	role: PropTypes.string
};
AvatarStep.defaultProps = {
	className: null,
	role: null
};
